import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Youtube, Loader2 } from "lucide-react";

const CHANNEL_ID = "UCpWDYDWoSQyzvkXfMe7PCyw";
const CHANNEL_URL = "https://www.youtube.com/@OurStrengthStories";
const SUBSCRIBE_URL = "https://www.youtube.com/@OurStrengthStories?sub_confirmation=1";
const RSS_URL = `https://www.youtube.com/feeds/videos.xml?channel_id=${CHANNEL_ID}`;
const RSS2JSON = "https://api.rss2json.com/v1/api.json?rss_url=";
const STORAGE_KEY = "commmmuniti_strength_videos";
const CACHE_TTL = 1000 * 60 * 30;

const FALLBACK_VIDEOS = [
    {
        id: "dQw4w9WgXcQ",
        title: "Our Strength Stories - Brand Growth & Digital Marketing",
        url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
        thumb: "https://i.ytimg.com/vi/dQw4w9WgXcQ/hqdefault.jpg",
        date: new Date().toISOString(),
    },
];

function readCache() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !Array.isArray(parsed.videos)) return null;
        if (Date.now() - parsed.ts > CACHE_TTL) return null;
        return parsed.videos;
    } catch (e) {
        return null;
    }
}

function writeCache(videos) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ videos, ts: Date.now() }));
    } catch (e) {
        // ignore storage errors
    }
}

function fetchWithTimeout(url, timeout = 12000) {
    return Promise.race([
        fetch(url),
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error("timeout")), timeout)
        ),
    ]);
}

async function fetchVideosWithRetry(maxRetries = 2) {
    const attempts = [
        { url: RSS2JSON + encodeURIComponent(RSS_URL), type: "json" },
        { url: RSS_URL, type: "xml" },
    ];

    for (let attempt = 0; attempt < maxRetries; attempt++) {
        for (const config of attempts) {
            try {
                const res = await fetchWithTimeout(config.url);
                if (!res.ok) continue;
                
                if (config.type === "json") {
                    const data = await res.json();
                    if (data.status === "ok" && data.items && data.items.length > 0) {
                        return data.items.slice(0, 6).map((item) => {
                            const videoId = item.guid?.replace("yt:video:", "") || 
                                           item.link?.match(/[?&]v=([^&]+)/)?.[1] || 
                                           item.link?.split("/").pop();
                            return {
                                id: videoId || item.guid || item.link,
                                title: item.title,
                                url: item.link,
                                thumb: item.thumbnail || `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
                                date: item.pubDate,
                            };
                        });
                    }
                } else {
                    const text = await res.text();
                    const xml = new DOMParser().parseFromString(text, "text/xml");
                    const entries = Array.from(xml.getElementsByTagName("entry")).slice(0, 6);
                    if (entries.length > 0) {
                        return entries.map((entry) => {
                            const linkEl = entry.getElementsByTagName("link")[0];
                            const thumbEl = entry.getElementsByTagName("media:thumbnail")[0];
                            const titleEl = entry.getElementsByTagName("title")[0];
                            const dateEl = entry.getElementsByTagName("published")[0];
                            const videoId = entry.getElementsByTagName("yt:videoId")[0]?.textContent;
                            return {
                                id: videoId || titleEl.textContent,
                                title: titleEl.textContent,
                                url: linkEl?.getAttribute("href") || `https://www.youtube.com/watch?v=${videoId}`,
                                thumb: thumbEl?.getAttribute("url") || `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
                                date: dateEl?.textContent,
                            };
                        });
                    }
                }
            } catch (e) {
                console.warn(`StrengthVideos fetch attempt failed:`, e.message);
            }
        }
    }
    return null;
}

export default function StrengthVideos() {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const refreshTimerRef = useRef(null);

    useEffect(() => {
        let active = true;
        const cached = readCache();

        if (cached && cached.length > 0) {
            setVideos(cached);
            setLoading(false);
        }

        const load = async () => {
            try {
                const items = await fetchVideosWithRetry(2);
                if (!active) return;
                if (items && items.length > 0) {
                    setVideos(items);
                    writeCache(items);
                } else if (!cached || cached.length === 0) {
                    setVideos(FALLBACK_VIDEOS);
                }
            } catch (e) {
                if (active && (!cached || cached.length === 0)) {
                    setVideos(FALLBACK_VIDEOS);
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        load();

        refreshTimerRef.current = setInterval(() => {
            fetchVideosWithRetry(1).then((items) => {
                if (items && items.length > 0) {
                    setVideos(items);
                    writeCache(items);
                }
            });
        }, CACHE_TTL);

        return () => {
            active = false;
            if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
        };
    }, []);

    if (loading && videos.length === 0) {
        return (
            <div className="flex items-center justify-center gap-3 py-12 text-white/70">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">Loading latest videos…</span>
            </div>
        );
    }

    const isFallback = videos.some((v) => FALLBACK_VIDEOS.some((f) => f.id === v.id));

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-8">
            {videos.map((v, i) => (
                <motion.a
                    key={v.id}
                    href={v.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`strength-video-${i}`}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{
                        duration: 0.7,
                        delay: i * 0.1,
                        ease: [0.16, 1, 0.3, 1],
                    }}
                    className="group relative rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03]"
                >
                    <div className="relative aspect-video overflow-hidden">
                        <img
                            src={v.thumb}
                            alt={v.title}
                            loading="lazy"
                            className="h-full w-full object-cover scale-105 group-hover:scale-100 transition-transform duration-[1200ms]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                        <div className="absolute bottom-3 left-3 right-3">
                            <h3 className="font-display text-[0.4375rem] leading-snug line-clamp-2">
                                {v.title}
                            </h3>
                        </div>
                        {isFallback && (
                            <div className="absolute top-2 right-2">
                                <span className="text-[10px] uppercase tracking-widest bg-white/20 text-white/80 px-2 py-1 rounded-full backdrop-blur-sm">
                                    Featured
                                </span>
                            </div>
                        )}
                    </div>
                </motion.a>
            ))}
        </div>
    );
}
